import { Fragment, useState, MouseEvent, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TaggedSeriousOffendersCard from '../../components/dashboard/roi/TaggedSeriousOffendersCard';
import TotalROICard from '../../components/dashboard/roi/TotalROICard';
import OverviewCard from '../../components/dashboard/roi/OverviewCard';
import TotalAlertsCard from '../../components/dashboard/roi/TotalAlertsCard';
import VisionsAwareCard from '../../components/dashboard/roi/VisionsAwareCard';
import useVisionAware from '../../hooks/useVisionAware';
import { VisionAwareContext } from '../../context/visionaware-context';
import BlueSkeleton from '../../components/shared/BlueSkeleton';
import RefreshButton from '../../components/shared/RefreshButton';
import DropDownButton, { DropDownMenu } from '../../components/shared/DropDownButton';
import { useTranslation } from 'react-i18next';
import DownloadButton from '../../components/shared/DownloadButton';
import axios from 'axios';
import Config from '../../global/env';
import Error from '../../components/shared/Error';
import SeriousOffenderMatchEventsCard from '../../components/dashboard/roi/SeriousOffenderMatchEventsCard';
import useAuthorizationStatus from '../../hooks/useAuthorizationStatus';
import { useLocation } from 'react-router-dom';

const timeFilterMenus: DropDownMenu[] = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 14 days', value: '14' },
    { label: 'Month-to-date', value: '30' },
    { label: 'Year-to-date', value: '365' }
];

const exportMenus: DropDownMenu[] = [{ label: 'PDF', value: 'pdf' }, { label: 'CSV', value: 'csv' }];

export default function VisionsAware() {
    const { t } = useTranslation();
    const [timeFilterRange, setTimeFilterRange] = useState(30);
    let { status, data, error, isFetching, refetch } = useVisionAware(timeFilterRange);
    const location = useLocation();
    const isAuthorized = useAuthorizationStatus(location.pathname, "visionAware");

    useEffect(() => {
        if(isAuthorized){
            const interval = setInterval(() => {
                loadData();
            }, 60000);
            return () => clearInterval(interval);
        }
    }, []);

    const timeRangeFilterChange = (e: MouseEvent<HTMLElement>, val: string) => {
        setTimeFilterRange(Number(val));
    }

    const buttonClickHandler = (e: MouseEvent<HTMLElement>) => {
        loadData();
    };

    const loadData = () => {
        if(isAuthorized){
            const apiURL = Config.apiBaseURL + Config.loadData;
            axios.get(apiURL)
                .then(() => {
                    refetch();
                })
                .catch(function (error) {
                    console.log(error);
                })
        }
    };

    return (
        <Fragment>

            { error ? <Error /> : <div><Stack spacing={1}>

                <Stack direction="row" 
                    sx={{ 
                        justifyContent: 'space-between',
                        flexWrap: 'wrap'
                    }}>

                    <Stack direction="row"
                        spacing={2}
                        divider={<Divider orientation="vertical" flexItem={true} />}
                        sx={{
                            p: 1,
                            backgroundColor: 'grey.400',
                            borderRadius: 3,
                            alignItems: 'center',
                            mb: {
                                xs: 1,
                                sm: 0
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                                fontSize: '16px',
                                textTransform: 'uppercase',
                                pl: 2,
                                pr: 1
                            }}
                        >{t('va_total_enrollments')}</Typography>
                        <Typography
                            color="secondary"
                                sx={{
                                fontWeight: '600',
                                fontSize: "32px",
                                pr: 2
                            }}
                        >
                            {isFetching && <BlueSkeleton width={80} />}
                            {status === 'success' && !isFetching && data?.totalEnrollments.toLocaleString()}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        
                        {/* <DropDownButton
                            boxShadow={false}
                            defaultLabel={t('app_export_data')}
                            color="text.primary"
                            backgroundColor="background.default"
                            fontSize={14}
                            data={exportMenus} />

                        <DownloadButton /> */}

                        <RefreshButton handler={buttonClickHandler} />

                        <Stack
                            direction="row"
                            sx={{
                                backgroundColor: 'white',
                                borderRadius: 2,
                                boxShadow: theme => '0px 0px 8px ' + theme.palette.grey.A200,
                                alignItems: 'center'
                            }}>
                            <Typography sx={{
                                pl: 1,
                                color: (theme) => theme.palette.secondary.main,
                                fontWeight: 'bold',
                                fontSize: 14,
                            }}>
                                Filter by:
                            </Typography>
                            <DropDownButton
                                boxShadow={false}
                                defaultLabel='Month-to-date'
                                fontSize={14}
                                data={timeFilterMenus}
                                color='primary.main'
                                fontWeight='bold'
                                handler={timeRangeFilterChange} />
                        </Stack>

                    </Stack>

                </Stack>

            </Stack>

            <VisionAwareContext.Provider value={{ status, data, error, isFetching: Boolean(isFetching), timeRange: timeFilterRange }}>
                <Grid container={true} columns={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12}} spacing={3} sx={{ mt: 3 }}>
                    <OverviewCard />
                    <VisionsAwareCard />
                    <Grid item={true} columns={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} 
                    
                    xs={12} sm={12} md={4} lg={4} xl={4} container={true} spacing={2} sx={{ height: '100%' }}>
                        <TotalAlertsCard />
                        <TotalROICard />
                    </Grid>
                    <Grid item={true} xs={12} sm={12} md={8} lg={8} xl={8} container={true} spacing={2}>
                        <TaggedSeriousOffendersCard />
                        <SeriousOffenderMatchEventsCard />
                    </Grid>
                </Grid>
            </VisionAwareContext.Provider></div>}
        </Fragment>
    )

}
